/**
 * ONE vocabulary for describing an order across every prediction-market feed.
 *
 * The opener's notifications page grew this language first — an EXECUTION leads
 * with a verb ("Sold 31.93 shares of Yes at 5.5¢") while a PLACEMENT never does,
 * so a feed can be skimmed for "what actually traded". The sniper's match feed
 * then rendered its own rows as `venue · market · size · @ price`, with no verb
 * and no phase, and a resting cover read exactly like a fill.
 *
 * Both surfaces now compose from here. Adding a second dialect — a new verb for
 * "sold", a second price format — is the drift this module exists to prevent.
 */

/**
 * Render a price (in dollars, e.g. 0.0551) as a Kalshi-style cents string
 * ("5.51¢"). Strips trailing zeros after the decimal so 0.5 becomes "50¢" and
 * 0.055 becomes "5.5¢" — matches the format Kalshi uses in their own
 * email/in-app notifications.
 */
export function centsFmt(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return '?';
  const cents = price * 100;
  // toFixed(2) gives a stable decimal then strip trailing 0s and a dangling
  // decimal point. 5.50 → "5.5", 50.00 → "50", 5.51 → "5.51".
  const s = cents.toFixed(2).replace(/\.?0+$/, '');
  return `${s}¢`;
}

/** "yes" / "YES" / "Yes" → "Yes". Falls back to "?" when missing. */
export function sideTitle(side: unknown): string {
  if (typeof side !== 'string' || side.length === 0) return '?';
  const lower = side.toLowerCase();
  if (lower === 'yes') return 'Yes';
  if (lower === 'no') return 'No';
  return side.charAt(0).toUpperCase() + side.slice(1).toLowerCase();
}

/**
 * Appends the YES/NO side to the platform token for fill / partial-fill rows,
 * e.g. `platform` "kalshi" + `side` "no" → "kalshi (NO)". The platform word
 * stays intact so `colorizePlatforms` still matches and colors it; the side tag
 * rides along as plain text right after it, satisfying "which side filled" at a
 * glance without reading into the fill phrase further down the row.
 */
export function platformWithSide(platform: string, side: unknown): string {
  if (!platform) return platform;
  const lower = typeof side === 'string' ? side.toLowerCase() : '';
  if (lower !== 'yes' && lower !== 'no') return platform;
  return `${platform} (${lower.toUpperCase()})`;
}

/**
 * "Sold" / "Bought" depending on action. Unwinding maker fills are always SELL
 * by construction (the engine sells to close positions), so when `action` is
 * missing we default to "Sold". Manual orders carry an explicit action in their
 * payload.
 */
export function actionVerb(action: unknown): 'Sold' | 'Bought' {
  if (typeof action !== 'string') return 'Sold';
  return action.toLowerCase() === 'buy' ? 'Bought' : 'Sold';
}

/**
 * Kalshi-email-style fill phrase: "Sold 31.93 shares of Yes at 5.5¢". Used for
 * both Kalshi and Polymarket fill events so the notifications page reads the
 * same way regardless of platform.
 */
export function fillPhrase(
  qty: number | string | undefined,
  side: unknown,
  price: number | null | undefined,
  action?: unknown,
): string {
  return `${actionVerb(action)} ${qty ?? '?'} shares of ${sideTitle(side)} at ${centsFmt(price ?? null)}`;
}

/**
 * Partial-fill variant: "Sold 31.93 of 100 shares of Yes at 5.5¢".
 *
 * When the original order size is unknown the producer can't supply a real
 * total and fabricates `totalQty === filledQty` (e.g. a polymarket.com
 * proxy-wallet maker fill synthesized from a single trade event —
 * `synthesizeUnknownMakerOrder`). Rendering "300 of 300" is self-contradictory
 * next to the 🟡 partial chip, so when the total is missing or not strictly
 * greater than the fill we drop the "of N" and tag "(partial)": we observed a
 * chunk but can't assert it's the whole order. Genuine partials always have
 * `filled < total`, so this branch never touches real maker/crossing partial
 * fills.
 */
export function partialFillPhrase(
  filledQty: number | string | undefined,
  totalQty: number | string | undefined,
  side: unknown,
  price: number | null | undefined,
  action?: unknown,
): string {
  if (isTotalUnknown(filledQty, totalQty)) {
    return `${actionVerb(action)} ${filledQty ?? '?'} shares of ${sideTitle(side)} at ${centsFmt(price ?? null)} (partial)`;
  }
  return `${actionVerb(action)} ${filledQty ?? '?'} of ${totalQty ?? '?'} shares of ${sideTitle(side)} at ${centsFmt(price ?? null)}`;
}

/** A total that can't qualify the fill: absent, unparseable, or ≤ the fill. */
function isTotalUnknown(
  filledQty: number | string | undefined,
  totalQty: number | string | undefined,
): boolean {
  const filledNum =
    typeof filledQty === 'string' ? parseFloat(filledQty) : filledQty;
  const totalNum = typeof totalQty === 'string' ? parseFloat(totalQty) : totalQty;
  return (
    totalNum == null ||
    Number.isNaN(totalNum) ||
    (filledNum != null && !Number.isNaN(filledNum) && filledNum >= totalNum)
  );
}

/** Which way an order goes. Drives the verb in every phrase below. */
export type OrderLegAction = 'buy' | 'sell';

/** The verb for a leg that HAS traded ("Bought" / "Sold"). */
function executedVerb(action: OrderLegAction): 'Bought' | 'Sold' {
  return actionVerb(action);
}

/** " at 98¢", or "" when the producer reported no price. */
function atPrice(price: number | null | undefined): string {
  return price == null || !Number.isFinite(price) ? '' : ` at ${centsFmt(price)}`;
}

/**
 * An EXECUTION, side-less: "Bought 30 of 200 shares at 95.13¢" (partial) or
 * "Sold 200 shares at 98¢" (complete). Same verbs, same "N of M shares at P¢"
 * grammar and same cents formatter as {@link fillPhrase}; the only clause
 * dropped is the YES/NO side, because the producers that use this form do not
 * put a side on the wire. Rendering "shares of Yes" from the strategy's usual
 * direction would be asserting a trading field we were never told.
 *
 * Complete-vs-partial is the SAME rule the opener uses: the "of M" qualifier
 * appears only when the total is known AND strictly greater than what filled
 * (a fill persisted before PRED-159 can EXCEED the original size after a
 * sale-and-rebuy cycle, and
 * "200 of 200" / "220 of 200" both read as broken).
 */
export function executedPhrase(
  action: OrderLegAction,
  filledQty: number | string | undefined,
  totalQty: number | string | undefined,
  price: number | null | undefined,
): string {
  const verb = executedVerb(action);
  if (isTotalUnknown(filledQty, totalQty)) {
    return `${verb} ${filledQty ?? '?'} shares${atPrice(price)}`;
  }
  return `${verb} ${filledQty ?? '?'} of ${totalQty ?? '?'} shares${atPrice(price)}`;
}

/**
 * A PLACEMENT: "Placed a sell order: 30 shares at 98¢ (nothing traded)".
 *
 * Deliberately does NOT lead with "Sold"/"Bought" — the whole point of the
 * split. The verb names the ACTION (placing), and the trailing "(nothing
 * traded)" is an assertion about THIS frame only, which is what makes it always
 * true: a placement frame reports a resting size, never an execution, whatever
 * the order may have done before or since.
 */
export function placedPhrase(
  action: OrderLegAction,
  qty: number | string | undefined,
  price: number | null | undefined,
): string {
  return `Placed a ${action} order: ${qty ?? '?'} shares${atPrice(price)} (nothing traded)`;
}

/**
 * An AMEND: "Repriced/resized the sell order: now 200 shares at 98¢ (nothing
 * traded)".
 *
 * The wire carries only the RESULTING price and size, never which of the two
 * moved, so the phrase names both possibilities rather than asserting a reprice
 * that may have been a resize. "now" marks the numbers as the resulting state
 * (the opener's `maker_adjusted` reads the same way), and "(nothing traded)"
 * answers the question "amended" on its own leaves open.
 */
export function amendedPhrase(
  action: OrderLegAction,
  qty: number | string | undefined,
  price: number | null | undefined,
): string {
  return `Repriced/resized the ${action} order: now ${qty ?? '?'} shares${atPrice(price)} (nothing traded)`;
}
